import Layer from "../Layer";
import { FontLoader } from "../../helpers";
import { FabricEllipse, FabricIText } from "../../objects";
import { CurvedTextLayerProps, CurvedTextProps } from "./interfaces";
import { LayerCustomType, TextCaseType } from "../../interfaces";
import {
  createPathObject,
  ellipseArcPath,
  getEllipseArcCenterPoint,
  pickProps,
  toTitleCase,
} from "../../utils";

export default class CurvedTextLayer extends Layer {
  protected static customType: LayerCustomType = LayerCustomType.CurvedText;
  protected static defaultTypeProps: Partial<CurvedTextProps> = {
    fill: "#000000",
    fontFamily: "Times New Roman",
    maxFontSize: 200,
    paintFirst: "stroke",
    stroke: "#000000",
    strokeWidth: 0,
    strokeLineJoin: "round",
    textAlign: "center",
    prefix: "",
    infix: "A",
    suffix: "",
    textCase: TextCaseType.None,
    startAngle: 45,
    endAngle: 135,
  };
  protected ellipseObject: FabricEllipse = new FabricEllipse({
    fill: "transparent",
    stroke: "black",
    strokeDashArray: [10, 10],
  });
  protected textObject: FabricIText;

  name: string = "Curved text layer";

  constructor(options: Partial<CurvedTextLayerProps> = {}) {
    const {
      transformSettings = {},
      typeSettings = {},
      ...layerProps
    } = options;
    super();

    const textOptions = {
      ...CurvedTextLayer.defaultTypeProps,
      ...typeSettings,
    };
    this.textObject = new FabricIText("", textOptions);

    this.insertAt(0, this.ellipseObject, this.textObject as any);

    this.set(transformSettings);
    this.set(layerProps);

    this.loadFont(this.textObject.fontSrc);
  }

  protected rerender() {
    const fontSizeScalable = this.textObject.fontSizeScalable;
    const fontSizeScale = fontSizeScalable
      ? Math.min(this.scaleX, this.scaleY)
      : 1;
    super.rerender();
    this.updateText();
    this.updatePath();
    this.updateFontSize(fontSizeScale);
    this.updatePosition();
    this.canvas?.requestRenderAll();
  }

  toObject() {
    const object = super.toObject();
    const textProps = this.getTextProps();

    return { ...object, typeSettings: textProps };
  }

  protected getTextWidth() {
    return this.textObject._measureLine(0).width;
  }

  protected updateText() {
    const {
      prefix = "",
      infix = "",
      suffix = "",
      textCase = "",
    } = this.textObject;

    let text = infix ? `${prefix}${infix}${suffix}` : "";
    if (textCase === "lowercase") {
      text = text.toLowerCase();
    } else if (textCase === "title-case") {
      text = toTitleCase(text);
    } else if (textCase === "uppercase") {
      text = text.toUpperCase();
    }

    this.textObject.set({ text });
    this.canvas?.requestRenderAll();
  }

  protected updatePath() {
    const { width: layerWidth, height: layerHeight } = this;
    const rx = layerWidth / 2;
    const ry = layerHeight / 2;
    const { startAngle, endAngle, pathSide } = this.textObject;
    const pathString = ellipseArcPath(
      rx,
      ry,
      startAngle,
      endAngle,
      pathSide === "left" ? false : true,
    );
    this.textObject.set({
      path: createPathObject(pathString, { visible: this.outlineVisible }),
    });
  }

  protected updateFontSize(scale = 1, deltaFontSize = 0.1) {
    this.textObject.maxFontSize *= scale;
    this.textObject.strokeWidth *= scale;
    this.textObject.charSpacing *= scale;

    const textPath = this.textObject.path;
    if (!textPath) return;

    const pathLength = textPath.segmentsInfo?.at(-1)?.length || 0;

    let low = 0;
    let high = this.textObject.maxFontSize;
    let bestFit = this.textObject.fontSize;

    while (high - low > deltaFontSize) {
      const mid = (low + high) / 2;
      this.textObject.set({ fontSize: mid });

      if (this.getTextWidth() <= pathLength) {
        bestFit = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    this.textObject.set({ fontSize: bestFit });
  }

  protected updatePosition() {
    const { width: layerWidth, height: layerHeight } = this;
    const {
      width: textWidth,
      height: textHeight,
      strokeWidth: textStrokeWidth,
      startAngle,
      endAngle,
      pathSide,
    } = this.textObject;

    const rx = layerWidth / 2;
    const ry = layerHeight / 2;
    const htsw = textStrokeWidth / 2;
    const { x, y } = getEllipseArcCenterPoint(rx, ry, startAngle, endAngle);
    this.textObject.set({
      left: (pathSide === "left" ? x : -x) - textWidth / 2 - htsw,
      top: (pathSide === "left" ? y : -y) - textHeight / 2 - htsw,
    });

    const hesw = this.ellipseObject.strokeWidth / 2;
    this.ellipseObject.set({
      rx: rx - hesw,
      ry: ry - hesw,
      top: -ry,
      left: -rx,
      visible: this.outlineVisible,
    });
  }

  setTextProps(props: Partial<CurvedTextLayerProps>) {
    this.textObject.set(props);
    this.rerender();
  }

  getTextProps(key?: string) {
    if (key) {
      return this.textObject.get(key);
    } else {
      const textProps = pickProps(this.textObject, [
        "prefix",
        "infix",
        "suffix",
        "fill",
        "fontPath",
        "fontSrc",
        "fontFamily",
        "fontSize",
        "maxFontSize",
        "textAlign",
        "textCase",
        "charSpacing",
        "lineHeight",
        "stroke",
        "strokeWidth",
        "pathSide",
        "startAngle",
        "endAngle",
        "globalCompositeOperation",
      ]);
      return textProps;
    }
  }

  async loadFont(fontSrc?: string) {
    this.textObject.set({ fontSrc });

    const fontFace = fontSrc
      ? await FontLoader.loadFontFace(fontSrc)
      : undefined;
    this.textObject.set({
      fontFamily:
        fontFace?.family || CurvedTextLayer.defaultTypeProps.fontFamily,
    });

    this.rerender();
  }
}

export * from "./interfaces";
